import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Bell,
  Briefcase,
  FolderTree,
  Home,
  LayoutGrid,
  LogOut,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const SECTION_ICONS: Record<string, LucideIcon> = {
  Home: Home,
  Tasks: LayoutGrid,
  Projects: FolderTree,
  CRM: Briefcase,
  People: Users,
};

const EXACT_BREADCRUMBS: Record<string, [string, string]> = {
  '/dashboard': ['Home', 'Dashboard'],
  '/tasks/board': ['Tasks', 'Board'],
  '/tasks/list': ['Tasks', 'List'],
  '/projects': ['Projects', 'Theme tree'],
  '/projects/archive': ['Projects', 'Archive'],
  '/crm': ['CRM', 'Pipeline'],
  '/crm/archive': ['CRM', 'Archive'],
  '/people/founders': ['People', 'Founders'],
  '/people/external': ['People', 'External'],
};

function breadcrumbFor(pathname: string): [string, string] | null {
  const exact = EXACT_BREADCRUMBS[pathname];
  if (exact) return exact;
  if (pathname.startsWith('/projects/task/')) return ['Projects', 'Task detail'];
  if (pathname.startsWith('/projects/theme/')) return ['Projects', 'Theme detail'];
  if (pathname.startsWith('/crm/contact/')) return ['CRM', 'Contact detail'];
  if (pathname.startsWith('/people/')) return ['People', 'Person detail'];
  return null;
}

const iconButtonBase: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  color: 'var(--color-text-primary)',
  padding: '8px',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  transition: 'color 120ms ease, background 120ms ease',
};

export default function Header() {
  const { logout, notifications, notificationCount, markNotificationsRead } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [bellOpen, setBellOpen] = useState(false);
  const bellWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!bellOpen) return;
    function handle(e: MouseEvent) {
      if (!bellWrapperRef.current) return;
      if (!bellWrapperRef.current.contains(e.target as Node)) {
        setBellOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [bellOpen]);

  const crumb = breadcrumbFor(pathname);
  const badgeText = notificationCount > 9 ? '9+' : String(notificationCount);

  return (
    <header
      style={{
        height: 'var(--header-height)',
        background: '#ffffff',
        borderBottom: '1px solid var(--color-border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        flexShrink: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <Link
          to="/dashboard"
          style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
        >
          <img
            src="/pnglogonotext.png"
            alt="Jambo Jira"
            style={{ height: '32px', width: 'auto', display: 'block' }}
          />
          <span
            style={{
              marginLeft: '8px',
              fontSize: '16px',
              fontWeight: 600,
              color: 'var(--color-navy-deep)',
            }}
          >
            Jambo Jira
          </span>
        </Link>
        <div
          style={{
            width: '1px',
            height: '24px',
            background: 'var(--color-border)',
          }}
        />
        {crumb && (() => {
          const SectionIcon = SECTION_ICONS[crumb[0]];
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px',
              }}
            >
              {SectionIcon && (
                <SectionIcon size={16} style={{ color: 'var(--color-text-muted)' }} />
              )}
              <span style={{ color: 'var(--color-text-muted)' }}>{crumb[0]}</span>
              <span style={{ color: 'var(--color-text-muted)' }}>/</span>
              <span style={{ color: 'var(--color-coral)', fontWeight: 600 }}>{crumb[1]}</span>
            </div>
          );
        })()}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div ref={bellWrapperRef} style={{ position: 'relative' }}>
          <button
            type="button"
            aria-label="Notifications"
            onClick={() => {
              setBellOpen((o) => {
                const next = !o;
                if (next) markNotificationsRead();
                return next;
              });
            }}
            style={{ ...iconButtonBase, position: 'relative' }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-coral)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-primary)';
            }}
          >
            <Bell size={22} />
            {notificationCount > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-4px',
                  minWidth: '18px',
                  height: '18px',
                  padding: '0 4px',
                  borderRadius: '9999px',
                  background: '#EF4444',
                  color: '#ffffff',
                  fontSize: '9px',
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                }}
              >
                {badgeText}
              </span>
            )}
          </button>

          {bellOpen && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                width: '320px',
                background: '#ffffff',
                boxShadow: 'var(--shadow-md)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius)',
                zIndex: 50,
                maxHeight: '400px',
                overflowY: 'auto',
              }}
            >
              <div
                style={{
                  fontSize: '14px',
                  fontWeight: 700,
                  padding: '12px 16px',
                  borderBottom: '1px solid var(--color-border)',
                  color: 'var(--color-text-primary)',
                }}
              >
                New since last login
              </div>
              {notifications.length === 0 ? (
                <div
                  style={{
                    padding: '24px',
                    textAlign: 'center',
                    color: 'var(--color-text-muted)',
                    fontSize: '13px',
                  }}
                >
                  No new tasks since your last login
                </div>
              ) : (
                notifications.map((task, idx) => {
                  const themeLabel = task.parent_theme_name
                    ? `${task.parent_theme_name} / ${task.theme_name ?? ''}`
                    : task.theme_name ?? '';
                  const isLast = idx === notifications.length - 1;
                  return (
                    <button
                      key={task.id}
                      type="button"
                      onClick={() => {
                        navigate(`/projects/task/${task.id}`);
                        setBellOpen(false);
                      }}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        padding: '12px 16px',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: isLast ? 'none' : '1px solid var(--color-border)',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.background =
                          'var(--color-warm-bg)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                      }}
                    >
                      <div
                        style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          color: 'var(--color-text-primary)',
                          marginBottom: '2px',
                        }}
                      >
                        {task.title}
                      </div>
                      {themeLabel && (
                        <div
                          style={{
                            fontSize: '12px',
                            color: 'var(--color-text-muted)',
                          }}
                        >
                          {themeLabel}
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          aria-label="Logout"
          onClick={() => logout()}
          style={iconButtonBase}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-coral)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-primary)';
          }}
        >
          <LogOut size={22} />
        </button>
      </div>
    </header>
  );
}
